import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, Rocket } from 'lucide-react'
import { useMemo, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { COMPANY_LEVEL_VALUE } from '@/components/form/project-select-field'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H3 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { appResourceTypeMap } from '@/config/app-resource-types'
import { gcpClusterMap } from '@/config/gcp-clusters'
import {
  useCompanyPath,
  useCreateFormProjectSelection,
  useIdCompany,
  useMixpanel,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import {
  type CreateAppFormValues,
  schema,
} from '@/routes/apps/create-app/form/schema'
import { AutoDeploy } from '@/routes/apps/create-app/form/sections/auto-deploy'
import { Location } from '@/routes/apps/create-app/form/sections/location'
import { Name } from '@/routes/apps/create-app/form/sections/name'
import { Overview } from '@/routes/apps/create-app/form/sections/overview'
import { Project } from '@/routes/apps/create-app/form/sections/project'
import { Resource } from '@/routes/apps/create-app/form/sections/resource'
import { Source } from '@/routes/apps/create-app/form/sections/source'
import {
  CREATE_APP_MUTATION,
  CREATE_APP_QUERY,
} from '@/routes/apps/create-app/gql'
import type {
  CreateApplicationMutation,
  CreateApplicationMutationVariables,
  CreateNewAppQuery,
  CreateNewAppQueryVariables,
} from '@/routes/apps/create-app/gql.types.gen'

export const CreateAppForm = () => {
  const { idCompany } = useIdCompany()
  const { rbac, totalServices, rolesInCompany } = useRBAC()
  const { navigateToPath } = useCompanyPath()
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()
  const [searchParams] = useSearchParams()

  const idProjectFromUrl = searchParams.get('idProject')

  const { data } = useSuspenseQuery<
    CreateNewAppQuery,
    CreateNewAppQueryVariables
  >(CREATE_APP_QUERY, { variables: { idCompany } })

  const [createApplication] = useMutation<
    CreateApplicationMutation,
    CreateApplicationMutationVariables
  >(CREATE_APP_MUTATION)

  // Filter projects by RBAC permission
  const projects = useMemo(() => {
    if (totalServices.totalProjects === 0) {
      return []
    }
    return (data?.company?.projects?.items ?? []).filter((project) =>
      rbac.company(idCompany).project(project.id).apps.create.can()
    )
  }, [
    data?.company?.projects?.items,
    idCompany,
    rbac,
    totalServices.totalProjects,
  ])

  const shouldShowProjectField = projects.length > 0

  // Company-level users can create apps at the company level (not just in projects)
  // They should be allowed to deselect a project to create a company-level app
  const isCompanyLevelUser = rbac.company(idCompany).apps.create.can()
  const { initialIdProject, isProjectOptional, schemaWithProjectRequirement } =
    useCreateFormProjectSelection({
      schema,
      rolesInCompany,
      isCompanyLevelUser,
      idProjectFromUrl,
      projects,
    })

  const form = useForm<CreateAppFormValues>({
    resolver: zodResolver(schemaWithProjectRequirement),
    shouldUnregister: false,
    criteriaMode: 'all',
    defaultValues: {
      idCompany,
      idProject: initialIdProject,
      autodeploy: true,
      idCluster: '',
      source: 'privateGit',
      gitType: 'github',
      defaultBranch: '',
      idRemoteRepository: '',
      repoUrl: undefined,
      dockerImage: '',
      idDockerRegistryCredential: undefined,
      displayName: '',
      idProcessResourceType: '',
    },
  })

  const { trigger } = form
  const onSubmit = async (values: CreateAppFormValues) => {
    const hasNoAccessToCreate = !rbac
      .company(idCompany)
      .project(values.idProject || '')
      .apps.create.can()

    if (hasNoAccessToCreate) {
      toast(
        <ToasterContent variant="error">
          You don't have permission to create an application
        </ToasterContent>,
        { position: 'top-right' }
      )
      return
    }

    const isValid = await trigger()
    if (isPending || !isValid) {
      return
    }
    startTransition(async () => {
      try {
        // Convert COMPANY_LEVEL_VALUE to undefined for API
        const idProject =
          values.idProject === COMPANY_LEVEL_VALUE
            ? undefined
            : values.idProject

        const res = await createApplication({
          variables: {
            idCompany,
            idProject,
            source: values.source,
            dockerImage: values.dockerImage,
            idDockerRegistryCredential: values.idDockerRegistryCredential,
            repoUrl: values.repoUrl,
            defaultBranch: values.defaultBranch,
            displayName: values.displayName,
            idCluster: values.idCluster,
            gitType: values.gitType,
            idProcessResourceType: values.idProcessResourceType,
          },
        })
        toast(
          <ToasterContent variant="success">
            Application created successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        track('App Created', {
          idApp: res.data?.createApplication?.id,
          location: gcpClusterMap[values.idCluster] ?? values.idCluster,
          buildType: values.source,
          resourceType:
            appResourceTypeMap[values.idProcessResourceType] ??
            values.idProcessResourceType,
        })

        navigateToPath({
          pathname: '/app/:appName/overview',
          variables: { appName: res.data?.createApplication?.name ?? '' },
        })
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create application
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Form {...form}>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-[2fr_1fr] pb-25">
        <div className="flex flex-col">
          <Card>
            <CardContent className="px-0">
              <H3 className="mb-6 px-6">Application details</H3>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="px-6 space-y-6">
                  <Source />
                  <AutoDeploy />
                </div>
                <Separator />
                <div className="px-6 space-y-6">
                  <Name />
                  {shouldShowProjectField ? (
                    <Project
                      projects={projects}
                      isAllowedToDeselect={isProjectOptional}
                    />
                  ) : null}
                  <Location />
                  <Resource />
                  <Button disabled={isPending}>
                    {isPending ? (
                      <Loader className="animate-spin" />
                    ) : (
                      <Rocket />
                    )}
                    Create application
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Overview />
        </div>
      </div>
    </Form>
  )
}
